import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { catchError, map, Observable, shareReplay, throwError } from 'rxjs';
import { PaginationModel, ProductModel, RequestParamModel, ResponseModel } from '@coreModels';
import { ApiConstants } from '@coreConstant';
import { environment } from 'src/environments/environment';
@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private groutColorCache = new Map<string, Observable<any>>();

  /**
   * Constructor for the class.
   *
   * @param {HttpClient} http - An instance of HttpClient for making HTTP requests.
   * @return {void}
   */
  constructor(private http: HttpClient) { }

  /**
   * Fetches the product list based on the given request parameters.
   *
   * @param {RequestParamModel} requestParamModel - The model containing the request parameters for the API call.
   * @param {HttpContext} [context] - Optional request context, e.g. SKIP_ERROR_TOAST.
   * @return {Observable<any>} An observable containing the paginated list of products.
   */
  getProduct(requestParamModel: RequestParamModel, context?: HttpContext): Observable<any> {
    return this.http.get(ApiConstants.PRODUCT_LIST_URL + requestParamModel.getParams(), { observe: 'response', context })
      .pipe(
        map<any, any>(response => {
          const responseData = new PaginationModel<ProductModel>();
          const headers = response.headers;
          responseData.setHeaders(headers);
          responseData.data = response.body.data as ProductModel[];
          return responseData;
        }),
        catchError((err: unknown) => {
          return throwError(err); // You can customize the error handling here
        })
      );
  }

  /**
   * Fetches a list of product collections based on the provided request parameters.
   *
   * @param {RequestParamModel} requestParamModel - The model containing the request parameters.
   * @return {Observable<PaginationModel<any>>} An observable containing a pagination model with the product collection data.
   */
  getProductCollectionList(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.PRODUCT_COLLECTION_LISTS_URL + requestParamModel.getParams(), { observe: 'response' })
      .pipe(map((response: any) => {
        const productList = new PaginationModel<any>();
        const headers = response.headers;
        productList.setHeaders(headers);
        productList.data = response.body.data as any[];
        return productList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * Fetches the list of product categories based on the provided request parameters.
   *
   * @param {RequestParamModel} requestParamModel - The model containing request parameters for the API call.
   * @return {Observable<PaginationModel<any>>} An observable containing a pagination model with the list of product categories.
   */
  getProductCategoryList(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.PRODUCT_CATEGORY_LISTS_URL + requestParamModel.getParams(), { observe: 'response' })
      .pipe(map((response: any) => {
        const productList = new PaginationModel<any>();
        const headers = response.headers;
        productList.setHeaders(headers);
        productList.data = response.body.data as any[];
        return productList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getProductTypeDetails(productTypeID: number, requestParamModel: any): any {
    return this.http.get(ApiConstants.PRODUCT_TYPE_DETAIL_URL + productTypeID, requestParamModel.getParams())
      .pipe(
        map<any, any>(response => {
          return response.data as ProductModel[];
        }),
        catchError(err => {
          throw err; // Rethrow the error for handling in the subscription
        })
      );
  }

  getConsumableCalculators(model: any) {
    return this.http.post<ResponseModel>(ApiConstants.PRODUCT_CONSUMABLE_CALCULATION_URL, model)
      .pipe(map(response => {
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * Fetches the product recommended color list based on the given request parameters.
   *
   * @param {RequestParamModel} requestParamModel - The model containing the request parameters for the API call.
   * @return {Observable<any>} An observable containing the paginated list of product recommended color lists.
   */
  getProductRecommendedColor(requestParamModel: RequestParamModel): Observable<any> {
    const cacheKey = requestParamModel.getParams();
    if (this.groutColorCache.has(cacheKey)) {
      return this.groutColorCache.get(cacheKey)!;
    }
    const obs = this.http.get(ApiConstants.PRODUCT_RECOMMENDED_COLOR_LIST_URL + cacheKey, { observe: 'response' })
      .pipe(
        map<any, any>(response => {
          const responseData = new PaginationModel<any>();
          const headers = response.headers;
          responseData.setHeaders(headers);
          responseData.data = response.body.data;
          return responseData;
        }),
        catchError((err: unknown) => throwError(err)),
        shareReplay(1)
      );
    this.groutColorCache.set(cacheKey, obs);
    return obs;
  }

  // download product sizing table
  downloadProductSizingTable(params: string): any {
    return this.http.get(ApiConstants.PRODUCT_DOWNLOAD_COLLECTION_SIZE_TABLE_URL + params)
      .pipe(map<any, any>(response => {
        return response.data;
      }),
        catchError((err: unknown) => {
          return throwError(err); // You can customize the error handling here
        }));
  }

  /**
   *
   * @param bodyData
   * @returns
   */
  download(bodyData: any) {
    return this.http.post(ApiConstants.PRODUCT_DOWNLOAD_URL, bodyData)
      .pipe(map<any, any>(response => {
        return response.data;
      }),
        catchError((err: unknown) => {
          return throwError(err); // You can customize the error handling here
        })
      );
  }

  /**
   *
   * @param bodyData
   * @returns
   */
  downloadFileContent(bodyData: any) {
    return `${environment.apiUrl}product/download-resource?id=${bodyData.id}&type=${bodyData.type}`;
  }

  downloadPriceListTable(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.PRODUCT_DOWNLOAD_PRICE_TABLE_URL + requestParamModel.getParams())
      .pipe(map<any, any>(response => {
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getProductDetails(productID: number, requestParamModel: RequestParamModel): Observable<ProductModel> {
    if (productID !== 0) {
      return this.http.get(ApiConstants.PRODUCT_DETAIL_URL + productID + requestParamModel.getParams())
        .pipe(map<any, ProductModel>(response => {
          return response.data as ProductModel;
        }), catchError(err => {
          throw err;
        }));
    } else {
      return this.http.get(ApiConstants.PRODUCT_LIST_URL + requestParamModel.getParams())
        .pipe(map<any, ProductModel>(response => {
          return response.data[0] as ProductModel;
        }), catchError(err => {
          throw err;
        }));
    }
  }

  getProductLeadTime(id: any, payload: any, isExportId?: boolean): any {
    const urlPath = isExportId ? 'lead-time-by-export-id' : 'lead-time';
    return this.http.post(`${ApiConstants.PRODUCT_DETAIL_URL}${urlPath}/${id}`, payload)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }
}
